
const BASE_URL = 'https://api.coinnexs.com/api';

export interface AdminTradePayload {
    userIds: string[];
    asset: string;
    principal: string;
    outcome: 'PROFIT' | 'LOSS' | 'DEPOSIT';
    settlementAmount: string;
}

export interface AdminTradeExecuteResponse {
    success: boolean;
    message?: string;
    data: {
        totalUsers: number;
        trades: {
            userId: string;
            tradeId: string;
            executedAt: string;
        }[];
    };
}

export interface AdminTradeLedgerEntry {
    userId: string;
    tradeId: number | string;
    asset: string;
    principal: string;
    outcome: 'PROFIT' | 'LOSS' | 'DEPOSIT';
    payout: string;
    executedAt: string;
}

export interface AdminAggregatedTrade {
    userId: string;
    userName: string;
    executionsCount: string;
    aggregatedPrincipal: string;
    finalSettlement: string;
    netPnl: string;
    trades: AdminTradeLedgerEntry[];
}

export interface AdminTradeDetail {
    id: string;
    oldId: number;
    userId: string;
    asset: string;
    principal: string;
    executedBy: string;
    executedAt: string;
    createdAt: string;
    result: {
        outcome: 'PROFIT' | 'LOSS' | 'DEPOSIT';
        settlementAmount: string;
    }
}

export interface AdminTradeDetailResponse {
    success: boolean;
    data: AdminTradeDetail;
}

class AdminTradingService {
    private async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
        const token = localStorage.getItem('coinnexs_token');
        const headers: Record<string, string> = {
            ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
            'Content-Type': 'application/json',
            ...(options.headers as Record<string, string>),
        };

        try {
            const response = await fetch(`${BASE_URL}${endpoint}`, {
                ...options,
                headers,
            });

            const data = await response.json();
            return data as T;
        } catch (error) {
            console.error(`Admin Trading API Error (${endpoint}):`, error);
            throw error;
        }
    }

    async getTradingLedger(): Promise<AdminAggregatedTrade[]> {
        // Based on Step 3: GET /admin/trades returns the aggregated structure
        return this.request<AdminAggregatedTrade[]>('/admin/trades');
    }

    async executeTrade(payload: AdminTradePayload): Promise<AdminTradeExecuteResponse> {
        return this.request<AdminTradeExecuteResponse>('/admin/trades/execute', {
            method: 'POST',
            body: JSON.stringify(payload),
        });
    }

    async getTradeDetails(tradeId: string): Promise<AdminTradeDetailResponse> {
        return this.request<AdminTradeDetailResponse>(`/admin/trades/${tradeId}`);
    }

    async deleteTrade(tradeId: string): Promise<{ success: boolean }> {
        return this.request<{ success: boolean }>(`/admin/trades/${tradeId}`, {
            method: 'DELETE',
        });
    }
}

export const adminTradingService = new AdminTradingService();
